import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, AlertTriangle, User } from 'lucide-react';
import { useUsernameCheck } from './username/useUsernameCheck';
import EncryptedBadge from './security/EncryptedBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UsernameManagement = () => {
  const { username, currentUsername, setCurrentUsername, checking, available, setAvailable, handleUsernameChange } = useUsernameCheck();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const onUsernameChange = (e) => {
    setError('');
    setSuccess('');
    handleUsernameChange(e);
  };

  const handleSave = async () => {
    if (!username) { setError('Please enter a username'); return; }
    if (username.length < 3 || username.length > 20) { setError('Username must be 3-20 characters'); return; }
    if (!/^[a-z][a-z0-9_-]*$/.test(username)) {
      setError('Username must start with a letter and contain only letters, numbers, underscore, or hyphen');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/users/username`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ username })
      });
      if (response.ok) {
        setSuccess('Username updated successfully!');
        setCurrentUsername(username);
        setAvailable(null);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to update username');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <User className="w-5 h-5" />
          <span>Username</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600">
          Set a username to login more easily. You can use either your email or username to sign in.
        </p>
        {currentUsername && (
          <p className="text-sm font-medium text-slate-700">
            Current username: <span className="text-emerald-600">{currentUsername}</span>
          </p>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Label htmlFor="username">Username</Label>
            <EncryptedBadge variant="inline" />
          </div>
          <div className="relative">
            <Input id="username" type="text" value={username} onChange={onUsernameChange}
              placeholder="Enter a username (3-20 characters)" className="pr-10" />
            {checking && <Loader2 className="absolute right-3 top-3 w-4 h-4 animate-spin text-slate-400" />}
            {!checking && available === true && <CheckCircle className="absolute right-3 top-3 w-4 h-4 text-green-500" />}
            {!checking && available === false && <AlertTriangle className="absolute right-3 top-3 w-4 h-4 text-red-500" />}
          </div>
          {available === false && <p className="text-xs text-red-600">Username already taken</p>}
          {available === true && <p className="text-xs text-green-600">Username available!</p>}
          <p className="text-xs text-slate-500">Must start with a letter. Can contain letters, numbers, underscore (_), or hyphen (-).</p>
        </div>
        <Button onClick={handleSave} disabled={loading || !username || available === false} className="w-full">
          {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>) : 'Save Username'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default UsernameManagement;
